import { CheckCircle2, Circle } from 'lucide-react';
import { Card, CardContent } from '@ooh/ui/components/card';
import { DataTable, type ColumnDef } from '@ooh/ui/components/data-table';
import {
  SourceFileSummaryBar,
  type SourceFileSummaryBarProps,
} from '@ooh/ui/components/source-file-summary-bar';
import { InfoHint } from '@ooh/ui/components/info-hint';
import { formatTemplate } from '@ooh/ui/lib/utils';

export interface HeaderRowSelectorRow {
  /** 1-based spreadsheet row number, exactly as a spreadsheet app would show it. */
  rowNumber: number;
  /** Raw cell values at this row, in file column order. Blank cells are `''`. */
  cells: string[];
}

export interface HeaderRowSelectorLabels {
  heading: string;
  helperText: string;
  rowNumberHeader: string;
  previewHeading: string;
  previewHeaderRowLabel: string;
  previewSampleRowLabel: string;
  previewNoSampleRow: string;
  emptyCellPlaceholder: string;
  emptyRowPlaceholder: string;
  /** Template with a `{row}` token, shown in the source bar while the
   * auto-detected row is still the selected one, e.g. "Nagłówek: wiersz {row}
   * (wykryto automatycznie)". */
  headerAutoNoteDetectedTemplate: string;
  /** Template with a `{row}` token, shown in the source bar once the operator
   * has picked a different row than the auto-detected one, e.g. "Nagłówek:
   * wiersz {row} (wybrano ręcznie)". */
  headerAutoNoteManualTemplate: string;
  /** aria-label + body text for the header-note's InfoHint affordance. */
  headerAutoInfoLabel: string;
  headerAutoInfoText: string;
}

export interface HeaderRowSelectorProps {
  /** File summary + sheet-picker card — `@ooh/ui`'s generic `SourceFileSummaryBar`
   * (the plain XLS importer's `MappingTransformsSourceBar` wraps the same
   * primitive — this feature may not import that component directly, per this
   * feature's own spec's cross-feature import ban). The header-row note is
   * built by THIS component instead (derived from `selectedRowIndex`/
   * `detectedRowIndex` below via `labels.headerAutoNote*Template`) so it can
   * never go stale relative to the row actually picked in the table — the
   * caller supplies every other source-bar label. */
  sourceBarProps: Omit<SourceFileSummaryBarProps, 'labels' | 'noteSlot' | 'testId'> & {
    labels: SourceFileSummaryBarProps['labels'];
  };
  /** First N raw rows of the uploaded sheet, in file order. */
  rows: HeaderRowSelectorRow[];
  /** 0-based index into `rows` that `detectHeaderRow` picked automatically. */
  detectedRowIndex: number;
  /** 0-based index into `rows` currently selected as the header row (starts equal to `detectedRowIndex`). */
  selectedRowIndex: number;
  onSelectRow: (rowIndex: number) => void;
  labels: HeaderRowSelectorLabels;
  testIdPrefix?: string;
}

/** Base-26 spreadsheet-style column letters (A, B, … Z, AA, AB, …) purely for visual
 * orientation — the operator doesn't know yet which raw column means what. */
function columnLetter(index: number): string {
  let n = index;
  let label = '';
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

/** Fixed per-column width for the raw-cell columns (A, B, C, …) — every column this
 * width keeps the grid visually even instead of each column auto-sizing to its own
 * widest sampled value (the previous auto-width layout produced ragged, inconsistent
 * column widths across the row). */
const CELL_COLUMN_WIDTH = 130;

function CellValue({ value, placeholder }: { value: string; placeholder: string }) {
  if (!value) return <span className="text-muted-foreground">{placeholder}</span>;
  return (
    <span className="block truncate" title={value}>
      {value}
    </span>
  );
}

/** Shared per-column-letter `ColumnDef`s for any "row of raw cell values" table below —
 * built once from the widest row so both the row picker and the header+sample preview
 * render identically instead of two different ad hoc grids. */
function buildCellColumns<T extends { cells: string[] }>(
  columnCount: number,
  emptyCellPlaceholder: string,
): ColumnDef<T>[] {
  return Array.from({ length: columnCount }, (_, col) => ({
    id: `col-${col}`,
    label: columnLetter(col),
    align: 'left' as const,
    width: CELL_COLUMN_WIDTH,
    renderCell: (row: T) => (
      <CellValue value={row.cells[col] ?? ''} placeholder={emptyCellPlaceholder} />
    ),
  }));
}

/** Row-number + selection-state cell, combined into a single leading column (the
 * operator asked for the row number to lead, not a separate marker column) — a plain
 * check/circle icon carries the selection state, no per-row text badge: which row is
 * CURRENTLY selected and whether that differs from the auto-detected one is already
 * stated once, in plain language, by the source bar's header note above — repeating it
 * as a badge on every row was redundant noise. */
function RowMarkerCell({ rowNumber, isSelected }: { rowNumber: number; isSelected: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {isSelected ? (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" strokeWidth={1.8} />
      ) : (
        <Circle className="h-4 w-4 shrink-0 text-muted-foreground/30" strokeWidth={1.8} />
      )}
      <span className="text-xs font-normal text-muted-foreground/60 tabular-nums">{rowNumber}</span>
    </div>
  );
}

/**
 * Manual header-row selector: shows the first N raw rows of an uploaded
 * sheet and lets the operator pick which one is the real header row,
 * overriding `detectHeaderRow`'s automatic pick when it guessed wrong (e.g.
 * a cover-note/banner block above the real table — see CU-86cb1npz2). The
 * detected row starts pre-selected; picking any other row is a manual
 * override.
 *
 * Built on `@ooh/ui`'s `DataTable`/`ColumnDef` (the same "raw rows/columns
 * grid" primitive the plain XLS importer's `mapping-transforms-preview-table.tsx`
 * uses) and `SourceFileSummaryBar` (the same generic file/sheet summary shell
 * `mapping-transforms-source-bar.tsx` wraps) — not a one-off grid, and not a
 * cross-feature import of the plain importer's own components (see this
 * feature's spec's cross-feature import ban). Row selection uses `DataTable`'s
 * `onRowClick` (whole-row click) plus a leading check-icon indicator column
 * rather than `rowSelection`, which is checkbox-shaped multi-select and
 * doesn't fit a single-choice pick.
 *
 * Presentational only — does not itself write
 * `AvailabilityProfile.headerRowIndex`.
 */
export function HeaderRowSelector({
  sourceBarProps,
  rows,
  detectedRowIndex,
  selectedRowIndex,
  onSelectRow,
  labels,
  testIdPrefix = 'header-row-selector',
}: HeaderRowSelectorProps) {
  const columnCount = Math.max(1, ...rows.map((row) => row.cells.length));
  const cellColumns = buildCellColumns<HeaderRowSelectorRow>(
    columnCount,
    labels.emptyCellPlaceholder,
  );
  const selectedRow = rows[selectedRowIndex];
  const sampleRow = rows[selectedRowIndex + 1];
  const isManualOverride = selectedRow !== undefined && selectedRowIndex !== detectedRowIndex;
  const headerAutoNote = selectedRow
    ? formatTemplate(
        isManualOverride
          ? labels.headerAutoNoteManualTemplate
          : labels.headerAutoNoteDetectedTemplate,
        { row: selectedRow.rowNumber },
      )
    : '';

  const rowColumns: ColumnDef<HeaderRowSelectorRow>[] = [
    {
      // No fixed `width` — the row-number label ("Wiersz") plus icon needs to size to
      // its own content; a fixed width here previously clipped the header label
      // ("Wiersz" -> "WIERS") since DataTable hides overflow whenever a width is set.
      id: 'rowNumber',
      label: labels.rowNumberHeader,
      align: 'left',
      fixed: true,
      renderCell: (row) => (
        <RowMarkerCell
          rowNumber={row.rowNumber}
          isSelected={row.rowNumber === selectedRow?.rowNumber}
        />
      ),
    },
    ...cellColumns,
  ];

  return (
    <div className="space-y-4" data-testid={testIdPrefix}>
      <SourceFileSummaryBar
        {...sourceBarProps}
        testId={`${testIdPrefix}-source-bar`}
        noteSlot={
          <div className="flex items-center gap-1.5" data-testid={`${testIdPrefix}-auto-note`}>
            <span className="text-xs text-muted-foreground">{headerAutoNote}</span>
            <InfoHint label={labels.headerAutoInfoLabel}>{labels.headerAutoInfoText}</InfoHint>
          </div>
        }
      />

      <div className="space-y-1.5">
        <p className="text-[13px] font-semibold text-foreground">{labels.heading}</p>
        <p className="text-xs text-muted-foreground">{labels.helperText}</p>
      </div>

      <div className="rounded-md border border-border/60">
        <DataTable
          columns={rowColumns}
          data={rows}
          getRowKey={(row) => String(row.rowNumber)}
          getRowTestId={(row) => `${testIdPrefix}-row-${row.rowNumber}`}
          getRowClassName={(row) =>
            // eslint-disable-next-line i18next/no-literal-string -- Tailwind class, not user-facing copy (see poi-list.tsx precedent)
            row.rowNumber === selectedRow?.rowNumber ? 'bg-primary/5' : undefined
          }
          onRowClick={(row) => onSelectRow(rows.indexOf(row))}
          resizableColumns={false}
        />
      </div>

      {/* Deliberately visually distinct from the picker table above (solid Card, tinted
       * background, larger top margin) instead of another bordered grid right underneath
       * it — the picker answers "which row did I choose", this answers "what does that
       * choice actually produce", and the two were blending into one wall of tables. */}
      <Card
        className="mt-8 border-l-4 border-l-primary bg-muted/50"
        data-testid={`${testIdPrefix}-preview`}
      >
        <CardContent className="p-4 space-y-3">
          <p className="text-[13px] font-semibold text-foreground">{labels.previewHeading}</p>
          {selectedRow ? (
            <div className="rounded-md border border-border bg-background">
              <DataTable
                columns={[
                  {
                    id: 'label',
                    label: '',
                    align: 'left',
                    width: 160,
                    fixed: true,
                    renderCell: (row: { id: string; label: string; cells: string[] }) => (
                      <span className="text-xs text-muted-foreground">{row.label}</span>
                    ),
                  },
                  ...buildCellColumns<{ id: string; label: string; cells: string[] }>(
                    columnCount,
                    labels.emptyCellPlaceholder,
                  ),
                ]}
                data={[
                  { id: 'header', label: labels.previewHeaderRowLabel, cells: selectedRow.cells },
                  ...(sampleRow
                    ? [
                        {
                          id: 'sample',
                          label: labels.previewSampleRowLabel,
                          cells: sampleRow.cells,
                        },
                      ]
                    : []),
                ]}
                getRowKey={(row) => row.id}
                resizableColumns={false}
              />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">{labels.emptyRowPlaceholder}</p>
          )}
          {selectedRow && !sampleRow ? (
            <p className="text-xs text-muted-foreground italic">{labels.previewNoSampleRow}</p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
